import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { baseService } from '@services/base.service';
import useUserPreferencesStore from '@store/UserPreferencesStore';
import type { UserPreferences } from '@common-types';

interface UserProfileResponse {
  userPreferences?: UserPreferences;
}

const POLL_INTERVAL_MS = 30_000;

export const usePreferencesSync = () => {
  const setUserPreferences = useUserPreferencesStore((state) => state.setUserPreferences);
  const prevFavoritesRef = useRef<string | undefined>(undefined);

  const { data } = useQuery<UserProfileResponse>({
    queryKey: ['preferences-sync'],
    queryFn: () => baseService.getAsync<UserProfileResponse>('/user/profile'),
    refetchInterval: POLL_INTERVAL_MS,
    staleTime: 0,
  });

  useEffect(() => {
    if (!data?.userPreferences) return;

    const favoritesKey = JSON.stringify(data.userPreferences.favorites);

    if (prevFavoritesRef.current === undefined) {
      prevFavoritesRef.current = favoritesKey;
      return;
    }

    if (favoritesKey !== prevFavoritesRef.current) {
      prevFavoritesRef.current = favoritesKey;
      setUserPreferences(data.userPreferences);
    }
  }, [data, setUserPreferences]);
};
